/* ═══════════════════════════════════════════════════════════════
   The Master Forma Studio — Section End palette.

   End of Section / Post-Body Zones / Blank Page — ported from the
   classic editor's inline "ds-eos" panel (app.tsx), restyled onto kit
   primitives (Switch, Input, SearchableSelect) instead of hand-rolled
   checkboxes. Same fields, same writes.

   NEW in the rebuild (contract §3 R6, item 2): bold / italic / underline
   / size controls for the END OF SECTION line itself
   (`special.eosStyle`) — the model and the engine already carry this
   (paper.tsx's <EndOfSection> already reads it); this panel is where a
   lay person turns it on. WYSIWYG law: what this panel sets is exactly
   what prints.
   ═══════════════════════════════════════════════════════════════ */

import React from 'react';
import type { StandardConfig, TextStyle } from '../../../shared/types';
import { Input } from '../../components/ui/input';
import { Switch } from '../../components/ui/switch';
import { Hint } from '../../components/ui/tooltip';
import { SearchableSelect } from '../../components/SearchableSelect';
import { FORMA_TIPS } from '../../formaTooltips';

function Toggle({
  checked,
  label,
  hint,
  onChange,
}: {
  checked: boolean;
  label: string;
  hint?: string;
  onChange: (v: boolean) => void;
}) {
  const id = React.useId();
  return (
    <div className="sfs-toggle-row">
      <Hint text={hint}>
        <label htmlFor={id} className="sfs-toggle-label">
          {label}
        </label>
      </Hint>
      <Switch id={id} checked={checked} onCheckedChange={onChange} />
    </div>
  );
}

export function EosPanel({
  forma,
  onEdit,
}: {
  forma: StandardConfig;
  onEdit: (fn: (f: StandardConfig) => StandardConfig) => void;
}) {
  const sp = forma.special || ({} as StandardConfig['special']);
  const eosOn = sp.eosEnabled !== false;
  const eosStyle: TextStyle = sp.eosStyle || {};
  const postOn =
    sp.postBodyEnabled ?? !!(sp.postLeft?.length || sp.postCenter?.length || sp.postRight?.length);
  const spacing = sp.zoneSpace?.eos?.before ?? sp.eosSpacing ?? sp.postBodySpacing ?? 24;

  const setSp = (fn: (s: StandardConfig['special']) => StandardConfig['special']) =>
    onEdit((f) => ({ ...f, special: fn(f.special) }));

  return (
    <div className="sfs-panel">
      <div className="sfs-panel-card">
        <span className="sfs-panel-eyebrow">End of section</span>
        <Toggle
          checked={eosOn}
          label="Show end of section"
          hint={FORMA_TIPS.eosEnabled}
          onChange={(v) => setSp((s) => ({ ...s, eosEnabled: v }))}
        />
        {eosOn && (
          <>
            <div className="sfs-field-row">
              <Hint text={FORMA_TIPS.eosSpacing}>
                <span className="sfs-field-label">Spacing</span>
              </Hint>
              <Input
                type="number"
                aria-label="End-of-section spacing, points"
                min={0}
                max={300}
                step={6}
                value={spacing}
                className="sfs-num-input"
                onChange={(e) => {
                  const nv = Math.max(0, Math.min(300, Number(e.target.value) || 0));
                  setSp((s) => ({
                    ...s,
                    eosSpacing: nv,
                    zoneSpace: {
                      ...(s.zoneSpace || {}),
                      eos: { ...(s.zoneSpace?.eos || {}), before: nv },
                    },
                  }));
                }}
              />
              <span className="sfs-field-suffix">pt</span>
            </div>
            <label className="sfs-field-label" htmlFor="sfs-eos-text">
              Text
            </label>
            <Input
              id="sfs-eos-text"
              value={sp.endOfSectionText ?? 'END OF SECTION'}
              onChange={(e) => setSp((s) => ({ ...s, endOfSectionText: e.target.value }))}
            />
            <div className="sfs-charbtn-row" role="group" aria-label="End-of-section emphasis">
              <button
                type="button"
                className={`sfs-charbtn${eosStyle.bold ? ' is-on' : ''}`}
                aria-pressed={!!eosStyle.bold}
                aria-label="Bold"
                title="Bold"
                onClick={() =>
                  setSp((s) => ({ ...s, eosStyle: { ...s.eosStyle, bold: !eosStyle.bold } }))
                }
              >
                B
              </button>
              <button
                type="button"
                className={`sfs-charbtn${eosStyle.italic ? ' is-on' : ''}`}
                aria-pressed={!!eosStyle.italic}
                aria-label="Italic"
                title="Italic"
                style={{ fontStyle: 'italic' }}
                onClick={() =>
                  setSp((s) => ({ ...s, eosStyle: { ...s.eosStyle, italic: !eosStyle.italic } }))
                }
              >
                I
              </button>
              <button
                type="button"
                className={`sfs-charbtn${eosStyle.underline ? ' is-on' : ''}`}
                aria-pressed={!!eosStyle.underline}
                aria-label="Underline"
                title="Underline"
                style={{ textDecoration: 'underline' }}
                onClick={() =>
                  setSp((s) => ({
                    ...s,
                    eosStyle: { ...s.eosStyle, underline: !eosStyle.underline },
                  }))
                }
              >
                U
              </button>
              <Input
                type="number"
                aria-label="End-of-section font size, points"
                min={6}
                max={72}
                value={eosStyle.size ?? ''}
                placeholder="Size"
                className="sfs-num-input"
                onChange={(e) => {
                  const raw = e.target.value;
                  setSp((s) => ({
                    ...s,
                    eosStyle: { ...s.eosStyle, size: raw === '' ? undefined : Number(raw) },
                  }));
                }}
              />
              <span className="sfs-field-suffix">pt</span>
            </div>
          </>
        )}
      </div>

      <div className="sfs-panel-card">
        <span className="sfs-panel-eyebrow">Post-body zones</span>
        <Toggle
          checked={postOn}
          label="Enable post-body zones"
          hint={FORMA_TIPS.postBodyEnabled}
          onChange={(v) => setSp((s) => ({ ...s, postBodyEnabled: v }))}
        />
        <p className="sfs-panel-hint">
          L/C/R zones after the body, before END OF SECTION. Most specs only need End of Section —
          turning this off hides the zones without deleting their content.
        </p>
      </div>

      <div className="sfs-panel-card">
        <span className="sfs-panel-eyebrow">Blank page</span>
        <Toggle
          checked={!!sp.blankPageEnabled}
          label="Enable blank page"
          hint={FORMA_TIPS.blankPageEnabled}
          onChange={(v) => setSp((s) => ({ ...s, blankPageEnabled: v }))}
        />
        {sp.blankPageEnabled && (
          <>
            <div className="sfs-field-row">
              <Hint text={FORMA_TIPS.blankPageCondition}>
                <span className="sfs-field-label">Apply</span>
              </Hint>
              <div style={{ flex: 1 }}>
                <SearchableSelect
                  value={sp.blankPageCondition || 'always'}
                  options={[
                    { value: 'always', label: 'Always' },
                    { value: 'whenOdd', label: 'When page count is odd' },
                    { value: 'whenEven', label: 'When page count is even' },
                  ]}
                  clearable={false}
                  searchPlaceholder="Search…"
                  onChange={(v) => setSp((s) => ({ ...s, blankPageCondition: v }))}
                />
              </div>
            </div>
            <label className="sfs-field-label" htmlFor="sfs-blank-text">
              Text
            </label>
            <Input
              id="sfs-blank-text"
              value={sp.intentionallyBlankText ?? ''}
              placeholder="This page intentionally left blank"
              onChange={(e) => setSp((s) => ({ ...s, intentionallyBlankText: e.target.value }))}
            />
            <div className="sfs-field-row">
              <span className="sfs-field-label">Alignment</span>
              <div
                className="sfs-charbtn-row"
                role="group"
                aria-label="Blank-page text alignment"
                style={{ flex: 1 }}
              >
                {(['left', 'center', 'right'] as const).map((j) => (
                  <button
                    key={j}
                    type="button"
                    className={`sfs-charbtn${(sp.blankPageJustify || 'center') === j ? ' is-on' : ''}`}
                    aria-pressed={(sp.blankPageJustify || 'center') === j}
                    aria-label={
                      j === 'left' ? 'Align left' : j === 'center' ? 'Center' : 'Align right'
                    }
                    title={j === 'left' ? 'Align left' : j === 'center' ? 'Center' : 'Align right'}
                    onClick={() => setSp((s) => ({ ...s, blankPageJustify: j }))}
                  >
                    {j === 'left' ? 'L' : j === 'center' ? 'C' : 'R'}
                  </button>
                ))}
              </div>
            </div>
            <div className="sfs-field-row">
              <Hint text="How far down the page the text sits — drag the text on the paper, or set it here.">
                <span className="sfs-field-label">Vertical position</span>
              </Hint>
              <Input
                type="number"
                aria-label="Blank-page text vertical position, percent"
                min={5}
                max={95}
                step={1}
                value={sp.blankPageVertical ?? 50}
                className="sfs-num-input"
                onChange={(e) => {
                  const nv = Math.max(5, Math.min(95, Number(e.target.value) || 50));
                  setSp((s) => ({ ...s, blankPageVertical: nv }));
                }}
              />
              <span className="sfs-field-suffix">%</span>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
